import { CareSitesApi } from "@/api/careSites/CareSitesApi";
import { queryKeys } from "@/api/queryKeys";
import { useMyCareSites } from "@/api/careSites/useCareSites";
import { useAuth } from "@/auth/AuthContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { sitesForOrganization, writableCareSites } from "@/ui/intake/intakeCareSite";
import { readLastCareSiteId, writeLastCareSiteId } from "@/ui/intake/lastCareSite";

/**
 * Stellen des Vereins, eingeschränkt auf Schreibrecht, plus die lokal gemerkte letzte Stelle.
 * Ohne angemeldeten Nutzer (Tests) bleibt die Vereinsliste, damit bestehende Aufrufer nicht leer werden.
 */
export function useIntakeCareSites(organizationId: string | undefined) {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const { sites, loading: sitesLoading } = useMyCareSites();
  const accessQ = useQuery({
    queryKey: queryKeys.careSites.myAccess(userId ?? ""),
    queryFn: () => CareSitesApi.listMyWritableSiteIds(),
    enabled: !!userId,
    staleTime: 15_000,
  });
  const lastQ = useQuery({
    queryKey: queryKeys.intake.lastCareSite(userId ?? "", organizationId ?? ""),
    queryFn: () => readLastCareSiteId(userId!, organizationId!),
    enabled: !!userId && !!organizationId,
    staleTime: 15_000,
  });

  const orgSites = useMemo(
    () => (organizationId ? sitesForOrganization(sites, organizationId) : []),
    [sites, organizationId],
  );
  const accessKnown = !userId || accessQ.isSuccess;
  const writable = useMemo(() => {
    if (!accessKnown) return orgSites.slice(0, 0);
    const ids = accessQ.isSuccess ? new Set(accessQ.data) : null;
    return writableCareSites(orgSites, ids);
  }, [accessKnown, accessQ.isSuccess, accessQ.data, orgSites]);

  const remember = useCallback(
    (careSiteId: string) => {
      if (!userId || !organizationId) return;
      void writeLastCareSiteId(userId, organizationId, careSiteId).then(() => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.intake.lastCareSite(userId, organizationId),
        });
      });
    },
    [organizationId, queryClient, userId],
  );

  return {
    sites: writable,
    loading:
      sitesLoading ||
      (!!userId && accessQ.isFetching && !accessQ.isSuccess) ||
      (!!userId && !!organizationId && lastQ.isFetching && lastQ.data === undefined),
    lastUsedId: lastQ.data ?? null,
    accessFailed: !!userId && accessQ.isError,
    remember,
  };
}
